import { defineStore } from "pinia";

import type { IdMap } from "@/model/baseTypes";
import type { HistoryEntry, SelectionAction, SelectionState } from "@/model/selection";

import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { useCardStore } from "./card";

export const useSelectionStore = defineStore("selection", {
  state: () => ({
    selecting: "no" satisfies "no" | "hover" | "click" | "rect",
    historyPerBoard: {} as IdMap<Array<HistoryEntry<SelectionState>>>,
  }),
  getters: {
    history: (state) => {
      const boardId = useBoardStore().currentBoard().id;
      if (!(boardId in state.historyPerBoard)) {
        state.historyPerBoard[boardId] = [];
      }
      return state.historyPerBoard[boardId];
    },
    lastHistoryEntry(): HistoryEntry<SelectionState> | undefined {
      return this.history[this.history.length - 1];
    },
    mirrorTargets() {
      const cards = useBoardStore().selectedStickyIds.map((id) => useCardStore().cards[id]);
      return useBoardsStore().mirrorTargetBoards(cards);
    },
    isSelectingWithRect: (state) => state.selecting === "rect",
  },
  actions: {
    async undo() {
      if (this.history.length === 0) {
        return Promise.resolve();
      }
      const [historyEntry] = this.history.splice(-1);
      return historyEntry.action.resetToState(historyEntry.stateBeforeAction) || Promise.resolve();
    },
    async execute<T extends SelectionState>(action: SelectionAction<T>) {
      const stateBeforeAction = action.saveState();
      this.history.push({ stateBeforeAction, action } as unknown as HistoryEntry<SelectionState>);
      return action.execute(stateBeforeAction) || Promise.resolve();
    },
  },
});
